import React, { useState, useRef, type MouseEvent, type SVGProps, useCallback, useMemo, useEffect } from 'react'
import {
  Box,
  Button,
  IconButton,
  Popover,
  TextField,
  CircularProgress,
  InputAdornment,
  Radio,
  Checkbox,
  Stack,
  Divider,
  Skeleton,
  Typography,
  Tooltip,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'

import { ReactComponent as CheckBoxIcon } from '@/assets/icons/checkbox-icon.svg'
import { ReactComponent as CheckBoxCheckedIcon } from '@/assets/icons/checkbox-checked-filled-icon.svg'
import { ReactComponent as RadioCheckedIcon } from '@/assets/icons/radio-checked-icon.svg'
import { ReactComponent as RadioUncheckedIcon } from '@/assets/icons/radio-unckecked-icon.svg'
import { handleGenerateMultipleChoiceOptions, replaceExistingOption } from '@/utils/generateMultipleChoiceOptions'
import { type CardActivityWithId } from '@/models/card-activity/card-activity.model'
import { CustomTooltip } from '@/components/ui/tooltip'
import { AIGenerateIcon } from '@/views/NewBuilder/components/IconsBuilder'
import { debounce } from '@/hooks/useDebounce'

interface ChoiceSelectorProps {
  card: CardActivityWithId
  updateCard: (id: string, property: keyof CardActivityWithId, newValue: Partial<CardActivityWithId>) => void
}

function ChoiceSelector({ card, updateCard }: ChoiceSelectorProps): JSX.Element {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [options, setOptions] = useState<CardActivityWithId['choices']>([
    { option: 'A', value: 'Answer A' },
    { option: 'B', value: 'Answer B' },
    { option: 'C', value: 'Answer C' },
    { option: 'D', value: 'Answer D' },
  ])
  const [answers, setAnswers] = useState<string[]>([])
  const [loadingOption, setLoadingOption] = useState<string | null>(null)
  const [generatingAll, setGeneratingAll] = useState(false)
  const optionCharCodeRef = useRef('D'.charCodeAt(0))
  const MCQType = card?.MCQType || 'single'
  const question = card?.question || ''

  function handleTogglePopover(event: MouseEvent<HTMLButtonElement>) {
    event.preventDefault()
    event.stopPropagation()
    setAnchorEl(anchorEl ? null : event.currentTarget)
  }

  function handleSelectAnswer(opt: string) {
    const updatedAnswers =
      MCQType === 'single' ? [opt] : answers.includes(opt) ? answers.filter(a => a !== opt) : [...answers, opt]

    setAnswers(updatedAnswers)
    updateCard(card.id, 'answer', updatedAnswers)
  }

  function handleDeleteOption(opt: string) {
    const updatedOptions = options.filter(o => o.option !== opt)
    const updatedAnswers = answers.filter(a => a !== opt)

    setOptions(updatedOptions)
    setAnswers(updatedAnswers)
    updateCard(card.id, 'choices', updatedOptions)
    updateCard(card.id, 'answer', updatedAnswers)
  }

  async function handleReplaceOption(opt: string) {
    try {
      setLoadingOption(opt)
      const { value } = await replaceExistingOption(options, opt)
      const updatedOptions = options.map(o => (o.option === opt ? { ...o, value } : o))

      setOptions(updatedOptions)
      updateCard(card.id, 'choices', updatedOptions)
    } finally {
      setLoadingOption(null)
    }
  }

  function handleAddOption(e: React.MouseEvent) {
    e.preventDefault()
    e.stopPropagation()
    const nextOption = String.fromCharCode(optionCharCodeRef.current++)
    const updatedOptions = [...options, { option: nextOption, value: 'Answer' }]

    setOptions(updatedOptions)
    updateCard(card.id, 'choices', updatedOptions)
  }

  async function handleGenerateOptions(e: React.MouseEvent) {
    e.preventDefault()
    e.stopPropagation()
    try {
      setGeneratingAll(true)
      const response = await handleGenerateMultipleChoiceOptions(question, MCQType === 'multi')
      const choices = response?.choices || []
      const answer = response?.answers || []

      setOptions(choices)
      setAnswers(answer)
      updateCard(card.id, 'choices', choices)
      updateCard(card.id, 'answer', answer)

      const maxChar = choices.reduce((max, c) => Math.max(max, c.option.charCodeAt(0)), 64)

      optionCharCodeRef.current = maxChar + 1
    } finally {
      setGeneratingAll(false)
    }
  }

  const debouncedUpdateCard = useMemo(() => {
    return debounce((id: string, field: keyof CardActivityWithId, value: Partial<CardActivityWithId>) => {
      updateCard(id, field, value)
    }, 500)
  }, [updateCard])

  useEffect(() => {
    return () => {
      debouncedUpdateCard.cancel?.()
    }
  }, [debouncedUpdateCard])

  useEffect(() => {
    setOptions(card.choices)
    setAnswers(card.answer)
  }, [card])

  const open = Boolean(anchorEl)
  const id = open ? 'choice-popover' : undefined

  return (
    <Box>
      <Button
        color="secondary"
        size="small"
        sx={{ borderRadius: '6px', fontSize: 12, height: 22, whiteSpace: 'nowrap' }}
        variant="outlined"
        onClick={handleTogglePopover}
      >
        View Choices
      </Button>
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        id={id}
        open={open}
        sx={{
          '& .MuiPaper-root': {
            borderRadius: '9.204px',
            background: '#FFF',
            boxShadow: '0px 1.221px 19.533px 0px rgba(16, 24, 40, 0.10) !important',
          },
        }}
        onClick={e => {
          e.preventDefault()
          e.stopPropagation()
        }}
        onClose={() => {
          setAnchorEl(null)
        }}
      >
        <Stack gap="9px" sx={{ padding: '12.271px 6.136px' }} width={360}>
          {options.map(({ option, value }) => {
            const isSelected = answers.includes(option)

            return loadingOption === option || generatingAll ? (
              <Skeleton key={option} height={45.13} sx={{ borderRadius: '10px' }} variant="rounded" width="100%" />
            ) : (
              <TextField
                key={option}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {MCQType === 'single' ? (
                        <Radio
                          checked={isSelected}
                          checkedIcon={<RadioCheckedIcon />}
                          icon={<RadioUncheckedIcon />}
                          size="small"
                          onClick={event => {
                            event.preventDefault()
                            event.stopPropagation()
                            handleSelectAnswer(option)
                          }}
                        />
                      ) : (
                        <Checkbox
                          checked={isSelected}
                          checkedIcon={<CheckBoxCheckedIcon />}
                          icon={<CheckBoxIcon />}
                          size="small"
                          onClick={event => {
                            event.preventDefault()
                            event.stopPropagation()
                            handleSelectAnswer(option)
                          }}
                        />
                      )}
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip arrow placement="left" title="Delete Option">
                        <IconButton
                          size="small"
                          onClick={event => {
                            event.preventDefault()
                            event.stopPropagation()
                            handleDeleteOption(option)
                          }}
                        >
                          <TrashIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip arrow placement="right" title="Refresh Option">
                        <IconButton
                          disabled={loadingOption === option}
                          size="small"
                          onClick={e => {
                            e.preventDefault()
                            e.stopPropagation()
                            handleReplaceOption(option)
                          }}
                        >
                          {loadingOption === option ? <CircularProgress size={16} /> : <RefreshIcon />}
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                margin="dense"
                size="small"
                sx={{
                  '&.MuiFormControl-root': { m: '0px' },
                  '& .MuiInputBase-root': { padding: '4px 8px' },
                  '& .MuiOutlinedInput-notchedOutline': { borderRadius: '10px !important' },
                }}
                value={value}
                variant="outlined"
                onChange={e => {
                  e.preventDefault()
                  e.stopPropagation()
                  const updatedOptions = options.map(o => (o.option === option ? { ...o, value: e.target.value } : o))

                  setOptions(updatedOptions)
                  debouncedUpdateCard(card.id, 'choices', updatedOptions)
                }}
                onClick={e => {
                  e.preventDefault()
                  e.stopPropagation()
                }}
              />
            )
          })}

          <Stack direction="row" display="flex" justifyContent="space-between">
            <Button
              startIcon={<AddIcon />}
              sx={{
                width: 'fit-content',
                color: '#C11574',
                fontSize: '14px',
                fontWeight: 500,
              }}
              variant="text"
              onClick={handleAddOption}
            >
              Add Option
            </Button>
            <Divider flexItem orientation="vertical" />
            <CustomTooltip title="AI creates choices based on the question. AI can make mistakes: ensure the options are correct.">
              <Button
                sx={{
                  width: 'fit-content',

                  fontSize: '14px',
                  fontWeight: 500,
                  gap: '4px',
                }}
                variant="text"
                onClick={handleGenerateOptions}
              >
                {generatingAll ? <CircularProgress color="primary" size={20} /> : <AIGenerateIcon />}
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 500,
                    color: 'transparent',
                    background: 'linear-gradient(45deg, #FF6C6C 0%, #DD7BFF 100%)',
                    backgroundClip: 'text',
                  }}
                >
                  {generatingAll ? 'Generating...' : 'Generate Options'}
                </Typography>
              </Button>
            </CustomTooltip>
          </Stack>
        </Stack>
      </Popover>
    </Box>
  )
}

export default ChoiceSelector

function TrashIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg fill="none" height={13} viewBox="0 0 13 13" width={13} xmlns="http://www.w3.org/2000/svg" {...props}>
      <g clipPath="url(#clip0_3442_15205)">
        <path
          d="M8.29738 3.31085V2.90181C8.29738 2.32909 8.29738 2.04273 8.18592 1.82398C8.08788 1.63156 7.93144 1.47512 7.73902 1.37708C7.52028 1.26562 7.23392 1.26562 6.6612 1.26562H5.84311C5.27039 1.26562 4.98403 1.26562 4.76528 1.37708C4.57287 1.47512 4.41643 1.63156 4.31838 1.82398C4.20693 2.04273 4.20693 2.32909 4.20693 2.90181V3.31085M5.22954 6.12304V8.67958M7.27477 6.12304V8.67958M1.65039 3.31085H10.8539M9.8313 3.31085V9.03749C9.8313 9.89657 9.8313 10.3261 9.66412 10.6542C9.51705 10.9429 9.28239 11.1775 8.99377 11.3246C8.66564 11.4918 8.23611 11.4918 7.37703 11.4918H5.12728C4.2682 11.4918 3.83866 11.4918 3.51054 11.3246C3.22191 11.1775 2.98725 10.9429 2.84019 10.6542C2.673 10.3261 2.673 9.89657 2.673 9.03749V3.31085"
          stroke="#98A2B3"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.02261}
        />
      </g>
      <defs>
        <clipPath id="clip0_3442_15205">
          <rect fill="white" height={12.2714} transform="translate(0.117188 0.242188)" width={12.2714} />
        </clipPath>
      </defs>
    </svg>
  )
}

function RefreshIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg fill="none" height={13} viewBox="0 0 13 13" width={13} xmlns="http://www.w3.org/2000/svg" {...props}>
      <g clipPath="url(#clip0_3201_96515)">
        <path
          d="M7.68102 1.26367C7.68102 1.26367 8.11524 1.3257 9.91234 3.1228C11.7094 4.91991 11.7094 7.83358 9.91234 9.63068C9.27562 10.2674 8.49874 10.6785 7.68102 10.8641M7.68102 1.26367L10.7489 1.26367M7.68102 1.26367L7.68102 4.33151M5.63579 11.4897C5.63579 11.4897 5.20157 11.4277 3.40447 9.63058C1.60737 7.83348 1.60737 4.9198 3.40447 3.1227C4.04118 2.48599 4.81807 2.07486 5.63579 1.88932M5.63579 11.4897L2.56795 11.4898M5.63579 11.4897L5.63579 8.42197"
          stroke="url(#paint0_linear_3201_96515)"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.02261}
        />
      </g>
      <defs>
        <linearGradient
          gradientUnits="userSpaceOnUse"
          id="paint0_linear_3201_96515"
          x1={2.0565}
          x2={12.2262}
          y1={11.4897}
          y2={2.33702}
        >
          <stop stopColor="#FF6C6C" />
          <stop offset={1} stopColor="#DD7BFF" />
        </linearGradient>
        <clipPath id="clip0_3201_96515">
          <rect fill="white" height={12.2714} transform="translate(0.523438 0.242188)" width={12.2714} />
        </clipPath>
      </defs>
    </svg>
  )
}
